/**
 * Presentation logic for the Clients screen (f6.1).
 *
 * Everything here is pure, so the rules that shape the table, its four toolbar
 * selects and its page controls are testable without rendering anything.
 * Nothing in this file decides what a caller may see or do: visibility is the
 * server's answer, and so are the status label, the currency label, the DM and
 * the inherited CM.
 *
 * **Every filter and the pagination run client-side**, over the set the server
 * returned whole. The collection is 39 rows and takes no `q`, `group`, `unit`,
 * `dm`, `status`, `page`, `page_size`, `sort` or `order` parameter -- which is
 * what keeps f4.1's *"no operation accepts a pagination, search, filter or
 * sort parameter"* assertion true verbatim for a **third** feature.
 *
 * The prototype is the first in this estate to draw real page controls
 * (*"Showing 1-12 of 39 clients"*, prev/1/2/3/next), so they are built -- 39
 * rows across four pages is a real experience. The revisit condition is
 * written down rather than left implicit: **when the collection outgrows the
 * browser the route grows a parameter**, and that is a decision taken with a
 * real number behind it rather than a discovery.
 */

import type { ActiveChildren, Client, CurrentOwner, InheritedOwner } from '@/api/client';
import { ApiError } from '@/api/client';
import { ownerFor } from '@/routes/business-unit-groups/ownershipView';
import { sortRows, type SortState, type SortValue } from '@/ui/tableSort';

/** The status code the screen styles as "good". Anything else is neutral. */
const ACTIVE = 'ACTIVE';

/**
 * The delivery role a Client supports.
 *
 * Named here only to LABEL and ORDER the client's own owner column, its DM
 * filter and the assign dialog's selector. The server refuses anything else
 * with `409 DELIVERY_ROLE_LEVEL_MISMATCH`, and it is the node-type registry --
 * not this array -- that decides what the assign dialog offers.
 *
 * **CM is deliberately absent from this list**, even though the table has a CM
 * column: a client's CM is its *unit's* CM, inherited and read-only. The
 * control that changes it lives on the screen that owns it.
 */
export const CLIENT_ROLE_CODES = ['DM'] as const;

export type ClientRoleCode = (typeof CLIENT_ROLE_CODES)[number];

/** The role a client inherits from its Business Unit, for the `CM` column. */
export const INHERITED_CM = 'CM';

/**
 * Rows per page, matching the prototype's *"Showing 1-12 of 39 clients"*.
 *
 * Twelve is the prototype's number, and against 39 rows it produces the four
 * pages its prev/1/2/3/next control draws.
 */
export const PAGE_SIZE = 12;

/**
 * The badge class for a status.
 *
 * Only the CLASS is derived from the code -- the LABEL always comes from the
 * server's `status.name`, so a status renamed in the lookup table renames
 * itself here with no frontend change (f1.3's rule, restated by f2.2).
 */
export function badgeClassFor(statusCode: string): string {
  return statusCode === ACTIVE ? 'badge pill-success' : 'badge pill-neutral';
}

export function isActive(client: Client): boolean {
  return client.status.code === ACTIVE;
}

/** The client's own current DM, or undefined. */
export function dmFor(client: Client): CurrentOwner | undefined {
  return ownerFor(client, 'DM');
}

/**
 * The owner of one role on one of the client's ANCESTORS, or undefined.
 *
 * Display only. It names who is accountable above this client and decides
 * nothing -- f9.1 owns the scope walk-down that does.
 */
export function inheritedOwnerFor(
  client: Client,
  roleCode: string,
): InheritedOwner | undefined {
  return (client.inherited_owners ?? []).find(
    (owner) => owner.delivery_role_code === roleCode,
  );
}

/**
 * Which column the table is sorted by.
 *
 * Every column the table renders is here except the one holding the row
 * actions, which has nothing to order by. `engagements` joined the list with
 * f7.1, which supplied the values -- the house rule being that every column a
 * table renders is sortable except a row-actions column.
 */
export type SortColumn =
  | 'code'
  | 'name'
  | 'unit'
  | 'group'
  | 'sapId'
  | 'DM'
  | 'CM'
  | 'engagements'
  | 'status';

/**
 * The default order: by name, as the server already sent it.
 *
 * `name` rather than `code`, because `name` is what the screen's second column
 * shows and what the estate identifies a client by -- `KKR` names two
 * different clients in the legacy and identifies neither.
 */
export const DEFAULT_SORT: SortState<SortColumn> = { column: 'name', direction: 'asc' };

/** What a column sorts by for one client. */
export function sortValueOf(client: Client, column: SortColumn): SortValue {
  switch (column) {
    case 'code':
      return client.code;
    case 'name':
      return client.name;
    case 'unit':
      return client.business_unit.name;
    case 'group':
      return client.business_unit_group.name;
    case 'sapId':
      return client.sap_id ?? null;
    case 'status':
      return client.status.name;
    case 'engagements':
      /* A NUMBER, so it sorts numerically rather than `10` before `2`. */
      return client.engagement_count;
    case 'CM':
      return inheritedOwnerFor(client, INHERITED_CM)?.display_name ?? null;
    default:
      return dmFor(client)?.display_name ?? null;
  }
}

/** The four toolbar selects. An empty string means "all". */
export interface ClientFilters {
  groupId: string;
  unitId: string;
  dmUserId: string;
  statusCode: string;
}

export const NO_FILTERS: ClientFilters = {
  groupId: '',
  unitId: '',
  dmUserId: '',
  statusCode: '',
};

/** Whether any of the four selects is narrowing the list. */
export function isFiltered(filters: ClientFilters): boolean {
  return Object.values(filters).some((value) => value !== '');
}

export interface TableView {
  search: string;
  filters: ClientFilters;
  sort: SortState<SortColumn>;
}

/**
 * Search across code, name, SAP ID AND alias, case-insensitively.
 *
 * The prototype's placeholder promises the first three -- *"Search client,
 * code or SAP ID"* -- and the alias is in the haystack for the reason f5.1 put
 * it there one level up: it is the only way to find the client a feed spelling
 * belongs to. An operator holding `Advent International Corporation` from a
 * Kimble dump should be able to type it and land on
 * `Advent International L.P.`
 */
export function applySearch(clients: readonly Client[], search: string): Client[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return [...clients];
  return clients.filter(
    (client) =>
      client.code.toLowerCase().includes(needle) ||
      client.name.toLowerCase().includes(needle) ||
      (client.sap_id ?? '').includes(needle) ||
      client.aliases.some((alias) => alias.toLowerCase().includes(needle)),
  );
}

/** The prototype's four selects, all client-side over the loaded set. */
export function applyFilters(
  clients: readonly Client[],
  filters: ClientFilters,
): Client[] {
  return clients.filter((client) => {
    if (filters.groupId && client.business_unit_group.id !== filters.groupId) {
      return false;
    }
    if (filters.unitId && client.business_unit.id !== filters.unitId) return false;
    if (filters.statusCode && client.status.code !== filters.statusCode) return false;
    if (filters.dmUserId && dmFor(client)?.user_id !== filters.dmUserId) return false;
    return true;
  });
}

export function applySort(
  clients: readonly Client[],
  sort: SortState<SortColumn>,
): Client[] {
  return sortRows(clients, sort, sortValueOf);
}

/** Everything that survives the search, the filters and the sort. */
export function visibleClients(
  clients: readonly Client[],
  view: TableView,
): Client[] {
  return applySort(
    applyFilters(applySearch(clients, view.search), view.filters),
    view.sort,
  );
}

/** One page of rows, and the numbers the controls render. */
export interface Page {
  rows: Client[];
  /** 1-based, clamped into range so a filter can never strand the reader. */
  page: number;
  pageCount: number;
  /** 1-based inclusive bounds, for *"Showing 1-12 of 39 clients"*. */
  from: number;
  to: number;
  total: number;
}

/**
 * The prototype's page controls, computed rather than typed.
 *
 * `page` is CLAMPED rather than trusted: narrowing a filter while on page 4
 * must not leave the reader staring at an empty table with no way back. An
 * empty set is page 1 of 1, so the controls render in a consistent shape
 * instead of disappearing.
 */
export function pageOf(rows: readonly Client[], page: number): Page {
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const current = Math.min(Math.max(1, page), pageCount);
  const start = (current - 1) * PAGE_SIZE;
  return {
    rows: rows.slice(start, start + PAGE_SIZE),
    page: current,
    pageCount,
    from: total === 0 ? 0 : start + 1,
    to: Math.min(start + PAGE_SIZE, total),
    total,
  };
}

/** The page numbers the control renders, always in ascending order. */
export function pageNumbers(pageCount: number): number[] {
  return Array.from({ length: pageCount }, (_unused, index) => index + 1);
}

export interface Option {
  id: string;
  name: string;
}

/**
 * The distinct parent units present in the loaded set, for the select.
 *
 * Read from the ROWS rather than from the units collection, for the reason
 * f4.2 gave about its owner filters and f5.1 repeated: a filter offering a
 * value that matches no row is a worse control than one offering the values
 * that do. The create dialog is the opposite case and reads the collection,
 * because there the question is what an Admin may choose.
 */
export function unitOptions(clients: readonly Client[]): Option[] {
  return distinct(clients.map((client) => client.business_unit));
}

/** The distinct grandparent groups present in the loaded set. */
export function groupOptions(clients: readonly Client[]): Option[] {
  return distinct(clients.map((client) => client.business_unit_group));
}

function distinct(labels: readonly { id: string; name: string }[]): Option[] {
  const byId = new Map<string, string>();
  for (const label of labels) byId.set(label.id, label.name);
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((left, right) => left.name.localeCompare(right.name));
}

/** The distinct statuses present, so the select never offers an empty filter. */
export function statusOptions(
  clients: readonly Client[],
): { code: string; name: string }[] {
  const byCode = new Map<string, string>();
  for (const client of clients) byCode.set(client.status.code, client.status.name);
  return [...byCode.entries()]
    .map(([code, name]) => ({ code, name }))
    .sort((left, right) => left.name.localeCompare(right.name));
}

/** The people who actually hold `DM` somewhere in the loaded set. */
export function dmOptions(
  clients: readonly Client[],
): { userId: string; displayName: string }[] {
  const byId = new Map<string, string>();
  for (const client of clients) {
    const dm = dmFor(client);
    if (dm) byId.set(dm.user_id, dm.display_name);
  }
  return [...byId.entries()]
    .map(([userId, displayName]) => ({ userId, displayName }))
    .sort((left, right) => left.displayName.localeCompare(right.displayName));
}

/**
 * The prototype's subtitle tail -- *"39 clients across 9 Business Units"* --
 * computed from the rows rather than typed.
 *
 * The prototype states that sentence as a fact; it is true of the legacy, but
 * this screen ships against an empty table and will show whatever the phase 8
 * import actually produces. Deriving it is what keeps the claim honest.
 */
export function clientSummary(clients: readonly Client[]): string {
  const units = new Set(clients.map((client) => client.business_unit.id));
  const clientWord = clients.length === 1 ? 'client' : 'clients';
  const unitWord = units.size === 1 ? 'Business Unit' : 'Business Units';
  return `${clients.length} ${clientWord} across ${units.size} ${unitWord}`;
}

/**
 * The children a refused deactivation named, or null.
 *
 * The client never computes this: it issues the deactivation WITHOUT
 * acknowledgement first and renders whatever the server refused with. Since
 * f7.1 registered `Engagement` against `Client`, the children it names are the
 * client's active engagements -- and the dialog started listing them with **no
 * change to this file**, which is what the design promised. f7.1's own
 * deactivate dialog reuses this function one level down, where the children
 * are delivery elements.
 */
export function activeChildrenFrom(error: unknown): ActiveChildren | null {
  return error instanceof ApiError ? error.activeChildren : null;
}

/** Whether a failure is a refusal by policy rather than something to retry. */
export function isRefusal(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

/** One spelling per line, as the prototype's textarea promises. */
export function linesToAliases(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/**
 * Re-exported, not redefined: reading f2.1's error envelope is the whole API's
 * concern, and the provenance formatters are shared with the levels above.
 */
export {
  PROVENANCE_MISSING,
  formatActor,
  formatMoment,
  initialsOf,
} from '@/routes/business-unit-groups/groupView';
export { generalMessage, problemFor } from '@/api/fieldErrors';
